// src/components/soldLeads/DealCheckDrawer.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  Box,
  HStack,
  VStack,
  Stack,
  Text,
  Badge,
  Tag,
  TagLabel,
  Divider,
  IconButton,
  Button,
  Link as ChakraLink,
  Switch,
  Tooltip,
  Skeleton,
  useToast,
} from '@chakra-ui/react';
import { ExternalLinkIcon, DownloadIcon } from '@chakra-ui/icons';
import { db } from '../../firebaseConfig';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import useOrgClaims from '../../hooks/useOrgClaims';
import { getAuth } from 'firebase/auth';
import SurveyManager from './SurveyManager';
import EmailActions from './footer/EmailActions';

/** ---------- tiny helpers (local to avoid import issues) ---------- */
const money = (n) => {
  const num = Number(n);
  if (!isFinite(num)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(num);
};
const pct = (n) => (isFinite(Number(n)) ? `${Number(n).toFixed(1)}%` : '—');
const nice = (v) => (v == null || v === '' ? '—' : String(v));
const yes = (v) => String(v || '').toLowerCase() === 'yes' || v === true;

/** ---------- slots & formatting ---------- */
const SLOT_LIST = [
  ['07:00', '09:00'],
  ['09:00', '11:00'],
  ['11:00', '13:00'],
  ['13:00', '15:00'],
  ['15:00', '17:00'],
  ['17:00', '19:00'],
  ['19:00', '21:00'],
];
const slotLabelFromStart = (hhmm) => {
  const row = SLOT_LIST.find(([start]) => start === hhmm);
  return row ? `${row[0]}–${row[1]}` : (hhmm ? `${hhmm} (Custom)` : '—');
};
const normaliseSlot = (s) =>
  s ? String(s).trim().replace(/\s*-\s*/g, '–').replace(/\s*–\s*/g, '–') : '';
const slotToString = (raw) => {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') return String(raw.label ?? raw.value ?? '');
  return String(raw);
};
const safeSlotLabel = (raw) => normaliseSlot(slotToString(raw));

/** ---------- header contact helpers ---------- */
const phoneHref = (p) => (p ? `tel:${String(p).replace(/[^\d+]/g, '')}` : '');
const buildCustomerName = (lead, row) => {
  if (row?.customer) return row.customer;
  const title = lead?.customerTitle ? `${lead.customerTitle} ` : '';
  const full = (lead?.customerFullName && String(lead.customerFullName).trim()) || '';
  const fn = String(lead?.customerFirstName || '').trim();
  const ln = String(lead?.customerLastName || '').trim();
  const name = full || `${fn} ${ln}`.trim();
  return (title + (name || '')).trim() || '—';
};

/** ---------- business logic helpers ---------- */
function computeAdminPaid(r) {
  const paidDate = (r?.adminPaidDate || '').trim();
  const tbpDate = (r?.adminTbpDate || '').trim();
  const method = (r?.adminPaymentMethod || '').trim();
  if (paidDate) return 'yes';
  if (tbpDate || method) return 'tbp';
  return 'na';
}

function checkTerms(report) {
  const type = String(report?.paymentType || '').toLowerCase();
  const reasons = [];
  const contract = Number(report?.contractPrice ?? 0);

  if (!type) return { ok: false, reasons: ['Payment type missing'] };

  if (type === 'cash') {
    const dep = Number(report?.cashDepositAmount ?? 0);
    if (!report?.cashTerm) reasons.push('Cash term missing');
    if (dep > 0 && !report?.cashDepositMethod) reasons.push('Cash deposit method missing');
  }

  if (type === 'finance') {
    const accepted =
      String(report?.financeResult || '').toLowerCase() === 'accepted' ||
      String(report?.finance2Result || '').toLowerCase() === 'accepted';
    const signed = yes(report?.financeApplicationSigned) || yes(report?.finance2Signed);
    if (!accepted) reasons.push('Finance not accepted');
    if (!signed) reasons.push('Finance not signed');

    const fin = Number(report?.financeAmount);
    if (isFinite(fin) && isFinite(contract) && contract && Math.abs(fin - contract) > 1) {
      reasons.push('Finance amount does not match contract');
    }
  }

  if (type === 'cash+finance' || type === 'cash & finance') {
    const cash = Number(report?.cfCashAmount);
    const fin = Number(report?.cfFinanceAmount);
    if (!isFinite(cash)) reasons.push('Cash amount missing');
    if (!isFinite(fin)) reasons.push('Finance amount missing');
    if (isFinite(cash) && isFinite(fin) && isFinite(contract) && contract && Math.abs(cash + fin - contract) > 1) {
      reasons.push('Cash + Finance does not add up to contract');
    }

    if (isFinite(fin) && fin > 0) {
      const accepted =
        String(report?.financeResult || '').toLowerCase() === 'accepted' ||
        String(report?.finance2Result || '').toLowerCase() === 'accepted';
      const signed = yes(report?.financeApplicationSigned) || yes(report?.finance2Signed);
      if (!accepted) reasons.push('Finance not accepted');
      if (!signed) reasons.push('Finance not signed');
    }
  }

  return { ok: reasons.length === 0, reasons };
}

function ReadyBadge({ ok }) {
  return ok ? (
    <Badge colorScheme="green" variant="solid" px={2} py={1}>Ready to book</Badge>
  ) : (
    <Badge colorScheme="orange" variant="subtle" px={2} py={1}>Needs attention</Badge>
  );
}

/** ---------- component ---------- */
export default function DealCheckDrawer({
  isOpen,
  onClose,
  row,
  onCopy,
  onForward,
  handlers,
}) {
  const toast = useToast();
  const { orgId } = useOrgClaims();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [lead, setLead] = useState(null);
  const [repPhone, setRepPhone] = useState('');
  const [isSurveySaving, setIsSurveySaving] = useState(false);

  useEffect(() => {
    let alive = true;

    async function load() {
      if (!isOpen || !orgId || !row?.leadId) return;
      setLoading(true);
      setReport(null);
      setLead(null);
      setRepPhone('');

      try {
        const repRef = doc(db, 'organizations', orgId, 'leads', row.leadId, 'salesReport', 'current');
        const leadRef = doc(db, 'organizations', orgId, 'leads', row.leadId);

        // Robust: don't crash if either read fails
        const [repRes, leadRes] = await Promise.allSettled([getDoc(repRef), getDoc(leadRef)]);
        if (!alive) return;

        if (repRes.status === 'fulfilled' && repRes.value.exists()) {
          setReport(repRes.value.data());
        } else {
          setReport(null);
        }

        if (leadRes.status === 'fulfilled' && leadRes.value.exists()) {
          const ld = leadRes.value;
          const leadData = { leadId: ld.id, ...(ld.data() || {}) };
          setLead(leadData);

          // fetch rep phone if we know salespersonId
          if (leadData?.salespersonId) {
            try {
              const spRef = doc(db, 'organizations', orgId, 'salespeople', leadData.salespersonId);
              const spSnap = await getDoc(spRef);
              if (spSnap.exists()) {
                const sp = spSnap.data();
                if (sp?.phone) setRepPhone(String(sp.phone));
              }
            } catch {
              // ignore salesperson lookup failure
            }
          }
        } else {
          setLead(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => { alive = false; };
  }, [isOpen, orgId, row?.leadId]);

  const view = useMemo(() => {
    const r = report || {};
    const attachments = r?.attachments || {};
    const contractVal = Number(r?.contractPrice ?? 0);
    const orderVal = Number(r?.orderPrice ?? 0);
    const adminVal = Number(
      r?.adminAdj !== undefined && r?.adminAdj !== '' ? r.adminAdj : (isFinite(orderVal) && isFinite(contractVal) ? orderVal - contractVal : NaN)
    );

    const contractPdf = attachments.contractPdfUrl || attachments.contractUrl || row?.contractPdfUrl || '';
    const doorsPdf = attachments.doorsPdfUrl || attachments.doorsUrl || row?.doorsPdfUrl || '';

    const adminPaid = computeAdminPaid(r);
    const { ok: termsOk, reasons: termReasons } = checkTerms(r);
    const fast = yes(r?.fastDelivery);
    const override = Boolean(lead?.surveyReadyOverride);

    const adminOk = adminPaid === 'yes' || adminPaid === 'na';
    const pdfOk = Boolean(contractPdf);
    const fastOk = fast || override;
    const ready = Boolean(adminOk && termsOk && pdfOk && fastOk);

    const reasons = [];
    if (!adminOk) reasons.push('Admin Paid not OK (Yes/N/A required)');
    if (!termsOk) reasons.push(...termReasons);
    if (!pdfOk) reasons.push('Contract PDF missing');
    if (!fastOk) reasons.push('Fast delivery not agreed (or override off)');

    const sellerSurvey = r?.survey || {};
    const sellerDate = sellerSurvey?.date || row?.surveyDate || lead?.surveyDate || '';

    // object-safe slot label (avoids “[object Object]”)
    let sellerSlot = safeSlotLabel(
      sellerSurvey?.time ?? sellerSurvey?.timeSlot ?? sellerSurvey?.slot ?? row?.surveySlot ?? ''
    );
    if (!sellerSlot && lead?.surveyTime) sellerSlot = slotLabelFromStart(lead.surveyTime);

    const paymentType = String(r?.paymentType || '').toLowerCase();

    return {
      adminPaid, contractPdf, doorsPdf, paymentType, termsOk, adminOk, pdfOk,
      fast, override, ready, reasons, sellerDate, sellerSlot, adminVal,
    };
  }, [report, row?.contractPdfUrl, row?.doorsPdfUrl, row?.surveyDate, row?.surveySlot, lead]);

  // Stage / Cancel gating
  const stageNormalized = useMemo(
    () => String(lead?.postSaleStage || '').toLowerCase().replace(/\s+/g, '_'),
    [lead?.postSaleStage]
  );
  const canCancelSale = Boolean(stageNormalized === 'awaiting_survey' && !isSurveySaving);
  const cancelTooltip = canCancelSale
    ? 'Cancel this sale'
    : 'Reset survey to "Awaiting" and Save before cancelling.';

  async function toggleOverride(next) {
    if (!orgId || !row?.leadId) return;
    try {
      const leadRef = doc(db, 'organizations', orgId, 'leads', row.leadId);
      const auth = getAuth();
      const user = auth?.currentUser || {};
      await updateDoc(leadRef, {
        surveyReadyOverride: next,
        surveyReadyOverrideByUid: next ? user.uid || null : null,
        surveyReadyOverrideByName: next ? (user.displayName || user.email || null) : null,
        surveyReadyOverrideAt: next ? serverTimestamp() : null,
      });
      setLead((prev) => ({ ...(prev || {}), surveyReadyOverride: next }));
      toast({ title: next ? 'Override enabled' : 'Override removed', status: 'success', duration: 1500 });
    } catch (e) {
      toast({ title: 'Update failed', description: e?.message || String(e), status: 'error' });
    }
  }

  async function handleSurveySave(payload) {
    if (!orgId || !row?.leadId) {
      throw new Error("Cannot save: Missing organization or lead ID.");
    }

    setIsSurveySaving(true);
    try {
      if (typeof handlers?.saveLeadField === "function") {
        await handlers.saveLeadField(row.leadId, payload);
      } else {
        const leadRef = doc(db, "organizations", orgId, "leads", row.leadId);
        await updateDoc(leadRef, payload);
      }

      // keep local state/cache + table row in sync
      setLead((prevLead) => ({ ...prevLead, ...payload }));
      if (typeof handlers?.updateRowField === "function") {
        handlers.updateRowField(row.leadId, payload);
      }

      toast({ title: 'Survey details saved', status: 'success', duration: 1400 });
      onClose();
    } finally {
      setIsSurveySaving(false);
    }
  }

  /** Header derived values */
  const customerName = useMemo(() => buildCustomerName(lead, row), [lead, row]);
  const customerPhone = useMemo(() => (lead?.customerPhone || row?.customerPhone || '').trim(), [lead, row]);
  const customerTel = useMemo(() => phoneHref(customerPhone), [customerPhone]);
  const addr = lead || row || {};

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        {/* Header */}
        <DrawerHeader color="blue.600">Deal Check & Survey Management Area</DrawerHeader>
        <DrawerBody>
          {loading ? (
            <Stack spacing={4}>
              <Skeleton height="24px" />
              <Skeleton height="20px" />
              <Skeleton height="120px" />
              <Skeleton height="24px" />
            </Stack>
          ) : (
            <VStack spacing={5} align="stretch">
              {/* Customer quick line */}
              {(customerName && customerName !== '—') && (
                <HStack justify="space-between" align="center" wrap="wrap">
                  <HStack spacing={3}>
                    <Text fontWeight="medium">
                      <Text as="span" color="gray.600" fontWeight="semibold">Customer:</Text>{' '}
                      {customerName}
                      {customerPhone && (
                        <>
                          {' · '}
                          <a href={customerTel} style={{ color: '#3182ce' }}>
                            📞 {customerPhone}
                          </a>
                        </>
                      )}
                    </Text>
                  </HStack>
                </HStack>
              )}

              {/* Status strip & shortcuts */}
              <HStack justify="space-between" align="center" wrap="wrap">
                <HStack spacing={3}>
                  <ReadyBadge ok={view.ready} />
                  {lead?.salespersonName && (
                    <Text fontWeight="medium">
                      <Text as="span" color="gray.600" fontWeight="semibold">Seller:</Text>{' '}
                      {lead.salespersonName}
                      {repPhone && (
                        <>
                          {' · '}
                          <a href={phoneHref(repPhone)} style={{ color: '#3182ce' }}>
                            📞 {repPhone}
                          </a>
                        </>
                      )}
                    </Text>
                  )}
                </HStack>
                <HStack>
                  <Tooltip label="Open Sales Report">
                    <IconButton
                      aria-label="Open Sales Report"
                      icon={<ExternalLinkIcon />}
                      size="sm"
                      onClick={() => window.open(`/reports/sales-report?leadId=${encodeURIComponent(row?.leadId || '')}`, '_blank')}
                    />
                  </Tooltip>
                  {view.contractPdf && (
                    <Tooltip label="Download Contract PDF">
                      <IconButton
                        aria-label="Download Contract"
                        icon={<DownloadIcon />}
                        size="sm"
                        as={ChakraLink}
                        href={view.contractPdf}
                        isExternal
                      />
                    </Tooltip>
                  )}
                </HStack>
              </HStack>

              <HStack>
                <Tag size="md" variant="subtle" colorScheme={view.adminPaid === 'yes' ? 'green' : (view.adminPaid === 'tbp' ? 'orange' : 'gray')}>
                  <TagLabel>{view.adminPaid === 'yes' ? 'Admin Paid: Yes' : view.adminPaid === 'tbp' ? 'Admin Paid: TBP' : 'Admin Paid: N/A'}</TagLabel>
                </Tag>
                <Tag size="md" variant="subtle" colorScheme={view.termsOk ? 'green' : 'orange'}>
                  <TagLabel>Terms: {view.termsOk ? 'OK' : 'check'}</TagLabel>
                </Tag>
                <Tag size="md" variant="subtle" colorScheme={view.contractPdf ? 'green' : 'orange'}>
                  <TagLabel>Contract PDF: {view.contractPdf ? 'present' : 'missing'}</TagLabel>
                </Tag>
                <Tag size="md" variant="subtle" colorScheme={view.fast ? 'green' : 'orange'}>
                  <TagLabel>Fast Delivery: {view.fast ? 'Yes' : 'No'}</TagLabel>
                </Tag>
              </HStack>

              <Box>
                <HStack>
                  <Switch
                    isChecked={Boolean(lead?.surveyReadyOverride)}
                    onChange={(e) => toggleOverride(e.target.checked)}
                  />
                  <Text fontSize="sm">
                    If "fast delivery" not agreed, click to override and book survey before the 14 days "cooling-off".
                  </Text>
                </HStack>
                {lead?.surveyReadyOverrideByName && (
                  <Text mt={1} fontSize="xs" color="gray.600">
                    Overridden by {lead.surveyReadyOverrideByName} • {lead?.surveyReadyOverrideAt ? 'just now' : ''}
                  </Text>
                )}
              </Box>

              <Divider />

              {/* Money */}
              <Box>
                <Text fontWeight="semibold" mb={2}>Money at a glance</Text>
                <HStack spacing={6} wrap="wrap">
                  <VStack spacing={0} align="start">
                    <Text fontSize="xs" color="gray.500">Contract</Text>
                    <Text>{money(report?.contractPrice)}</Text>
                  </VStack>
                  <VStack spacing={0} align="start">
                    <Text fontSize="xs" color="gray.500">Admin</Text>
                    <Text>{money(view.adminVal)}</Text>
                  </VStack>
                  <VStack spacing={0} align="start">
                    <Text fontSize="xs" color="gray.500">Order</Text>
                    <Text>{money(report?.orderPrice)}</Text>
                  </VStack>
                  <VStack spacing={0} align="start">
                    <Text fontSize="xs" color="gray.500">Discount</Text>
                    <Text>{pct(report?.discountPercent)}</Text>
                  </VStack>
                </HStack>
              </Box>

              {/* Admin Paid */}
              <Box>
                <Text fontWeight="semibold" mb={2}>Admin Paid</Text>
                <VStack align="start" spacing={1}>
                  <Text>Status: {view.adminPaid === 'yes' ? 'Yes' : view.adminPaid === 'tbp' ? 'TBP' : 'N/A'}</Text>
                  {view.adminPaid === 'yes' && (
                    <HStack>
                      <Text>Paid date:</Text><Text>{nice(report?.adminPaidDate)}</Text>
                      <Text>Method:</Text><Text>{nice(report?.adminPaymentMethod)}</Text>
                    </HStack>
                  )}
                  {view.adminPaid === 'tbp' && (
                    <HStack>
                      <Text>Target date:</Text><Text>{nice(report?.adminTbpDate)}</Text>
                      <Text>Planned method:</Text><Text>{nice(report?.adminPaymentMethod)}</Text>
                    </HStack>
                  )}
                </VStack>
              </Box>

              {/* Evidence */}
              <Box>
                <Text fontWeight="semibold" mb={2}>Evidence & Extras</Text>
                <VStack align="start" spacing={1}>
                  <HStack>
                    <Text>Contract PDF:</Text>
                    {view.contractPdf ? (
                      <ChakraLink href={view.contractPdf} isExternal color="blue.500">
                        Download <DownloadIcon mx="2px" />
                      </ChakraLink>
                    ) : (
                      <Text color="red.500">Missing</Text>
                    )}
                  </HStack>
                  {view.doorsPdf && (
                    <HStack>
                      <Text>Doors PDF:</Text>
                      <ChakraLink href={view.doorsPdf} isExternal color="blue.500">
                        Download <DownloadIcon mx="2px" />
                      </ChakraLink>
                    </HStack>
                  )}
                  <Text>Contract Number: {nice(report?.contractNumber)}</Text>
                </VStack>
              </Box>

              {/* Special arrangements */}
              {report?.specialArrangements && (
                <Box>
                  <Text fontWeight="semibold" mb={2}>Special Arrangements</Text>
                  <Box bg="gray.50" border="1px solid" borderColor="gray.200" rounded="md" p={3}>
                    <Text whiteSpace="pre-wrap" fontSize="sm">{report.specialArrangements}</Text>
                  </Box>
                </Box>
              )}

              {/* Seller survey intent */}
              {(view.sellerDate || view.sellerSlot) && (
                <Box>
                  <Text fontWeight="semibold" mb={2}>Seller Booked Survey</Text>
                  <HStack>
                    <Text>Date:</Text><Text>{nice(view.sellerDate)}</Text>
                    <Text>Slot:</Text><Text>{nice(view.sellerSlot)}</Text>
                  </HStack>
                </Box>
              )}

              {/* Admin survey manager */}
              <SurveyManager
                row={lead || row}
                onSave={handleSurveySave}
                isSaving={isSurveySaving}
                canEdit={true}
              />
            </VStack>
          )}
        </DrawerBody>

        {/* Footer */}
        <DrawerFooter>
          <HStack spacing={3} flexWrap="nowrap" align="center">
            {/* Email actions as a separate block (To intentionally blank in the mail app) */}
            <EmailActions
              row={lead || row}
              report={report}
              orgId={orgId}
              onCopy={(r) => onCopy?.(r)}
              onForward={(r) => {
                toast({ title: 'Marked as forwarded', status: 'success', duration: 1400 });
                onForward?.(r);
              }}
            />

            {/* Cancel Sale — only at awaiting_survey */}
            <Tooltip label={canCancelSale ? 'Cancel this sale' : 'Reset Survey, Save, and then Cancel.'} isDisabled={canCancelSale}>
              <span>
                <Button
                  size="sm"
                  colorScheme="red"
                  onClick={() => {
                    handlers?.openCancelSale?.(row);
                    onClose();
                  }}
                  isDisabled={!canCancelSale}
                >
                  Cancel Sale
                </Button>
              </span>
            </Tooltip>

            <Button size="sm" variant="ghost" onClick={onClose}>
              Close
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
